import type { InfiniteData, UseInfiniteQueryResult } from '@tanstack/react-query';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useSuspenseQueryOperation } from 'api/Query';
import { useCommit } from 'ts/base/hooks/UseCommit';
import { useProjectId } from 'ts/base/hooks/UseProject';
import type { SortOptions } from 'ts/base/table/SortableTable';
import type { IssueLikeQueryableEntityType } from 'ts/commons/query/QueryableEntityType';
import type { EKeyedObjectTypeEntry } from 'typedefs/EKeyedObjectType';
import type { IssueQueryResult } from 'typedefs/IssueQueryResult';

/**
 * Provides an infinite query with the results of the issue/spec item query. The component will suspend while loading
 * the first chunk of data, but does not use the error boundary by default.
 */
export function useIssueQueryResult(
	activeQuery: string,
	sortOptions: SortOptions,
	issueLikeType: IssueLikeQueryableEntityType,
	maxResults?: number,
	throwOnError = false
): UseInfiniteQueryResult<InfiniteData<IssueQueryResult>> {
	const projectId = useProjectId();
	const commit = useCommit();
	return useInfiniteQuery({
		queryKey: ['issue-query', projectId, activeQuery, sortOptions],
		queryFn: ({ pageParam }) =>
			issueLikeType.performQuery(projectId, activeQuery, pageParam, maxResults, sortOptions, commit),
		initialPageParam: 0,
		throwOnError,
		getNextPageParam(lastPage: IssueQueryResult): number | undefined {
			if (lastPage.resultSize <= lastPage.nextStartIndex) {
				return undefined;
			}
			return lastPage.nextStartIndex;
		}
	});
}

/** Returns all columns that are known to exist for issues of the specified type in the current project. */
export function useKnownColumns(issueLikeType: IssueLikeQueryableEntityType): Record<string, EKeyedObjectTypeEntry> {
	const projectId = useProjectId();
	const commit = useCommit();
	return useSuspenseQueryOperation(issueLikeType.getKnownColumns(projectId, commit));
}
